// ===========================================
// "These weeks have already been used" — the 409 WINDOW_HAS_DATA answer
// ===========================================
// The server REFUSES a later start / earlier last week / later student start over weeks that
// already hold attendance, clinic bookings or make-ups ("if attendance was marked they
// were in the lesson"). A toast is too small for the reasons list, so this is a dialog. It is a
// refusal, not a question: there is no override button, on purpose.

import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, List, ListItem } from '@mui/material';

export interface WindowHasDataInfo {
  message: string;
  reasons: string[];
}

/**
 * Reads a 409 `WINDOW_HAS_DATA` out of a thrown axios-style error, or `null` for anything else.
 * Shape (backend AppError): `response.data = { message, details: { code, reasons, … } }`.
 */
export function parseWindowHasData(err: unknown): WindowHasDataInfo | null {
  const response = (
    err as {
      response?: {
        status?: number;
        data?: { message?: string; details?: { code?: string; reasons?: unknown } };
      };
    }
  )?.response;
  if (response?.status !== 409 || response.data?.details?.code !== 'WINDOW_HAS_DATA') return null;
  const reasons = Array.isArray(response.data.details.reasons)
    ? response.data.details.reasons.filter((r): r is string => typeof r === 'string')
    : [];
  return { message: response.data.message ?? 'Some of these weeks have already been used.', reasons };
}

interface WindowHasDataDialogProps {
  /** `null` = closed. */
  info: WindowHasDataInfo | null;
  onClose: () => void;
  /** A sentence shown first, saying what DID happen (e.g. the invoice was still created). */
  note?: string;
}

export default function WindowHasDataDialog({ info, onClose, note }: WindowHasDataDialogProps) {
  return (
    <Dialog open={!!info} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="window-has-data-title">
      <DialogTitle id="window-has-data-title">These weeks have already been used</DialogTitle>
      <DialogContent>
        {note && (
          <Typography variant="body1" sx={{ mb: 1 }}>
            {note}
          </Typography>
        )}
        <Typography variant="body1" sx={{ mb: info?.reasons.length ? 1 : 0 }}>
          {info?.message}
        </Typography>
        {!!info?.reasons.length && (
          <List dense disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
            {info.reasons.map((reason, index) => (
              <ListItem key={index} sx={{ display: 'list-item', px: 0, py: 0.25 }}>
                {reason}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
